import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCostCenterReports } from "@/hooks/use-fleet-reports";
import { useAuth } from "@/hooks/use-auth";
import { useReportExport } from "@/hooks/use-report-export";
import { Download, Building2, Users, Truck, DollarSign } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { ReportHeader } from "@/components/fleet/reports/ReportHeader";

export function CostCenterReports() {
  const { activeCompany } = useAuth();
  const { data: costCenterReports, isLoading, error } = useCostCenterReports(activeCompany?.id);
  const { exportReport, isExporting } = useReportExport();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-8 bg-muted rounded w-1/2"></div>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !costCenterReports) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Erro ao carregar dados dos centros de custo.</p>
        </CardContent>
      </Card>
    );
  }

  // Calculate summary metrics
  const totalCostCenters = costCenterReports.length;
  const totalVehicles = costCenterReports.reduce((sum, cc) => sum + cc.total_vehicles, 0);
  const totalDrivers = costCenterReports.reduce((sum, cc) => sum + cc.total_drivers, 0);
  const totalCost = costCenterReports.reduce((sum, cc) => sum + cc.total_cost, 0);

  // Prepare chart data
  const costCenterChart = costCenterReports
    .sort((a, b) => b.total_cost - a.total_cost)
    .slice(0, 10)
    .map(cc => ({
      name: cc.cost_center_name,
      total_cost: cc.total_cost,
      fuel_cost: cc.fuel_cost,
      maintenance_cost: cc.maintenance_cost,
      vehicles: cc.total_vehicles
    }));

  const costDistribution = costCenterReports.map(cc => ({
    name: cc.cost_center_name,
    value: cc.total_cost,
    color: `hsl(${Math.floor(Math.random() * 360)}, 70%, 50%)`
  }));

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const handleExport = async (format: 'excel' | 'pdf') => {
    await exportReport({
      data: costCenterReports || [],
      type: 'cost-centers',
      format: format,
      company: activeCompany,
      filename: `relatorio-centros-custo-${Date.now()}.${format === 'pdf' ? 'pdf' : 'xlsx'}`
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Relatórios por Centro de Custo</h2>
          <p className="text-muted-foreground">
            Análise de custos e alocação de recursos por centro de custo
          </p>
        </div>
        <Button size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exportar Relatório
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Centros de Custo</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCostCenters}</div>
            <p className="text-xs text-muted-foreground">
              Centros ativos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Veículos Alocados</CardTitle>
            <Truck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalVehicles}</div>
            <p className="text-xs text-muted-foreground">
              Total da frota
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Motoristas Alocados</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalDrivers}</div>
            <p className="text-xs text-muted-foreground">
              Total de motoristas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custo Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalCost)}</div>
            <p className="text-xs text-muted-foreground">
              Soma de todos os centros
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Custos por Centro de Custo</CardTitle>
            <CardDescription>
              Comparativo de gastos entre centros de custo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={costCenterChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    formatCurrency(value as number),
                    name === 'total_cost' ? 'Custo Total' :
                    name === 'fuel_cost' ? 'Combustível' : 'Manutenção'
                  ]}
                />
                <Bar dataKey="fuel_cost" stackId="a" fill="hsl(var(--primary))" />
                <Bar dataKey="maintenance_cost" stackId="a" fill="hsl(var(--secondary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Distribuição de Custos</CardTitle>
            <CardDescription>
              Participação de cada centro de custo no total
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={costDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => percent > 5 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {costDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value as number)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhamento por Centro de Custo</CardTitle>
          <CardDescription>
            Análise detalhada de recursos e custos por centro
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Centro de Custo</TableHead>
                <TableHead className="text-right">Veículos</TableHead>
                <TableHead className="text-right">Motoristas</TableHead>
                <TableHead className="text-right">Combustível</TableHead>
                <TableHead className="text-right">Manutenção</TableHead>
                <TableHead className="text-right">Custo Total</TableHead>
                <TableHead className="text-right">Custo/km</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {costCenterReports.map((costCenter) => (
                <TableRow key={costCenter.cost_center_id}>
                  <TableCell className="font-medium">{costCenter.cost_center_name}</TableCell>
                  <TableCell className="text-right">{costCenter.total_vehicles}</TableCell>
                  <TableCell className="text-right">{costCenter.total_drivers}</TableCell>
                  <TableCell className="text-right">{formatCurrency(costCenter.fuel_cost)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(costCenter.maintenance_cost)}</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(costCenter.total_cost)}</TableCell>
                  <TableCell className="text-right">
                    {costCenter.cost_per_km > 0 
                      ? formatCurrency(costCenter.cost_per_km)
                      : 'N/A'
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}